import React, { useState, useEffect, useCallback } from "react";
import DashboardNav from "../DashboardNavbar/DashboardNav";
import { getAreas, getSubRegions,deleteRegion } from "../../api/Api";
import {
  Box,
  Typography,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Paper,
  Skeleton,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AddAreasModal from "../../components/modal/AddAreasModal";
import UniversalModal from "../../components/modal/UniversalModal";
import UpdateSubregion from "../../components/modal/UpdateSubregion";

const ROWS_PER_PAGE_OPTIONS = [5, 10, 25];
const INITIAL_ROWS_PER_PAGE = 5;

const TableSkeleton = ({ rowsCount = 5, cellsCount = 4 }) => (
  Array.from(new Array(rowsCount)).map((_, index) => (
    <TableRow key={index}>
      {Array.from(new Array(cellsCount)).map((_, idx) => (
        <TableCell key={idx} align="center">
          <Skeleton variant="text" width={idx === 2 ? 200 : 100} />
        </TableCell>
      ))}
    </TableRow>
  ))
);

const ManageAreas = () => {
  const theme = useTheme();
  const [state, setState] = useState({
    loading: false,
    regions: [],
    modalStates: {
      addRegion: false,
      updateArea: false,
      universal: false
    },
    pagination: {
      page: 0,
      rowsPerPage: INITIAL_ROWS_PER_PAGE
    },
    info: "",
    selectedRegion: null,
    deletingRegions: {} // Track loading state for delete buttons
  });

  const fetchRegionsWithSubRegions = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true }));
    
    try {
      const { data: { regions } } = await getAreas();
      
      const regionsWithSubRegions = await Promise.all(
        regions.map(async (region) => {
          try {
            const { data } = await getSubRegions(region.region_name);
            return {
              ...region,
              subRegions: data?.subRegions || []
            };
          } catch (error) {
            console.error(`Error fetching sub-regions for ${region.region_name}:`, error);
            return { ...region, subRegions: [] };
          }
        })
      );

      setState(prev => ({ ...prev, regions: regionsWithSubRegions, loading: false }));
    } catch (error) {
      console.error("Error fetching regions:", error);
      showMessage("Error fetching regions. Please try again.");
      setState(prev => ({ ...prev, loading: false }));
    }
  }, []);

  useEffect(() => {
    fetchRegionsWithSubRegions();
  }, [fetchRegionsWithSubRegions]);

  const showMessage = (message) => {
    setState(prev => ({
      ...prev,
      info: message,
      modalStates: { ...prev.modalStates, universal: true }
    }));
  };

  const handleModalClose = (modalKey) => {
    setState(prev => ({
      ...prev,
      modalStates: { ...prev.modalStates, [modalKey]: false },
      selectedRegion: modalKey === 'updateArea' ? null : prev.selectedRegion
    }));
  };

  const handleEditRegion = (regionId) => {
    const regionToEdit = state.regions.find(region => region._id === regionId);
    setState(prev => ({
      ...prev,
      selectedRegion: regionToEdit,
      modalStates: { ...prev.modalStates, updateArea: true }
    }));
  };

  const handleUpdateSuccess = async (updatedData) => {
    try {
      // First refresh the data
      await fetchRegionsWithSubRegions();
      
      // Then show success message and close modal
      showMessage("Region Updated Successfully");
      handleModalClose('updateArea');
    } catch (error) {
      console.error("Error refreshing data after update:", error);
      showMessage("Update successful but failed to refresh data. Please reload the page.");
      handleModalClose('updateArea');
    }
  };
  const handleDeleteRegion = async (regionId) => {
    setState(prev => ({
      ...prev,
      deletingRegions: { ...prev.deletingRegions, [regionId]: true }
    }));
  
    try {
      const response = await deleteRegion({ regionId: regionId });  // Pass as an object
      
      if (response.data?.success) {
        showMessage("Region deleted successfully");
        await fetchRegionsWithSubRegions();
      } else {
        showMessage(response.data?.message || "Failed to delete region");
      }
    } catch (error) {
      console.error("Error deleting region:", error);
      showMessage(error.response?.data?.message || "Error deleting region");
    } finally {
      setState(prev => ({
        ...prev,
        deletingRegions: { ...prev.deletingRegions, [regionId]: false }
      }));
    }
  };

  const handlePaginationChange = (key, value) => {
    setState(prev => ({
      ...prev,
      pagination: {
        ...prev.pagination,
        [key]: key === 'rowsPerPage' ? parseInt(value, 10) : value,
        page: key === 'rowsPerPage' ? 0 : prev.pagination.page
      }
    }));
  };

  const { loading, regions, modalStates, pagination, info, selectedRegion,deletingRegions  } = state;
  const { page, rowsPerPage } = pagination;

  return (
    <Box sx={{ height: "100vh" }}>
      <Box sx={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000 }}>
        <DashboardNav />
      </Box>

      <Box sx={{
        position: "absolute",
        top: "180px",
        left: 0,
        right: 0,
        px: 1,
        width: "90vw",
        margin: "auto",
        color: "#888585"
      }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: "500",
            fontSize: "32px",
            fontFamily: theme.typography.fontFamily
          }}
          gutterBottom
        >
          Manage Areas
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, height: "3rem" }}>
          <Button
            variant="contained"
            sx={{
              backgroundColor: theme.palette.primary.light,
              color: "#fff",
              textTransform: "capitalize",
              "&:hover": { backgroundColor: "#0056b3" }
            }}
            onClick={() => setState(prev => ({
              ...prev,
              modalStates: { ...prev.modalStates, addRegion: true }
            }))}
          >
            Add Region
          </Button>
        </Box>

        <TableContainer component={Paper} sx={{ mt: 4 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: theme.palette.primary.light }}>
              {["Sl No", "Region", "Sub-Regions", "Actions"].map((heading, idx) => (
                <TableCell key={idx} sx={{ color: "#fff", textAlign: "center" }}>
                  {heading}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableSkeleton />
            ) : regions.length ? (
              regions
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((region, index) => (
                  <TableRow key={region._id}>
                    <TableCell align="center">{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell align="center">{region.region_name}</TableCell>
                    <TableCell align="center">
                      {region.subRegions?.length 
                        ? region.subRegions.map(sub => sub.region_name || sub.name).join(", ")
                        : "No sub-regions"}
                    </TableCell>
                    <TableCell align="center" sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                      <Button
                        variant="outlined"
                        onClick={() => handleEditRegion(region._id)}
                        sx={{
                          color: "#fff",
                          backgroundColor: "#FF893A",
                          "&:hover": { backgroundColor: "#FF879A" },
                          textTransform: "capitalize"
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => handleDeleteRegion(region._id)}
                        disabled={deletingRegions[region._id]}
                        sx={{
                          color: "#fff",
                          backgroundColor: "#dc3545",
                          "&:hover": { backgroundColor: "#c82333" },
                          textTransform: "capitalize"
                        }}
                      >
                        {deletingRegions[region._id] ? (
                          <CircularProgress size={24} color="inherit" />
                        ) : (
                          "Delete"
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
            ) : (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  No regions available.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

        <TablePagination
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          component="div"
          count={regions.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => handlePaginationChange('page', newPage)}
          onRowsPerPageChange={(e) => handlePaginationChange('rowsPerPage', e.target.value)}
        />
      </Box>

      <AddAreasModal
        open={modalStates.addRegion}
        handleClose={() => handleModalClose('addRegion')}
        onSuccess={() => {
          fetchRegionsWithSubRegions();
          showMessage("Region Added Successfully");
          handleModalClose('addRegion');
        }}
      />

      <UpdateSubregion
        open={modalStates.updateArea}
        handleClose={() => handleModalClose('updateArea')}
        initialRegion={selectedRegion?.region_name}
        initialSubRegions={selectedRegion?.subRegions || []}
        regionId={selectedRegion?._id}
        onSuccess={handleUpdateSuccess}
      />

      <UniversalModal
        open={modalStates.universal}
        onClose={() => setState(prev => ({
          ...prev,
          modalStates: { ...prev.modalStates, universal: false }
        }))}
        message={info}
      />
    </Box>
  );
};

export default ManageAreas;